import axiosInstance from './axiosInstance';

/**
 * 파트너 (캐스타와 함께하는 업체)
 *
 * 종류에 따라 상세 화면 구성이 달라진다.
 *   PRODUCTION  제작사   — 필모그래피 / 소속 배우
 *   AGENCY      소속사   — 소속 배우
 *   ACADEMY     교육기관 — 학과·학회 / 배우·작품
 *   ETC         기타     — 할인 혜택 / 갤러리
 */
export const PARTNER_TYPES = [
  { key: 'ALL', label: '전체' },
  { key: 'PRODUCTION', label: '제작사' },
  { key: 'AGENCY', label: '소속사' },
  { key: 'ACADEMY', label: '교육기관' },
  { key: 'ETC', label: '기타' },
];

export const PARTNER_TYPE_LABEL = {
  PRODUCTION: '제작사',
  AGENCY: '소속사',
  ACADEMY: '교육기관',
  ETC: '기타',
};

/**
 * 파트너가 가진 종류들을 배열로 돌려준다.
 *
 * 한 업체가 제작사이면서 소속사인 경우가 있어 종류는 여러 개다.
 * 서버가 types를 주기 전에는 type 하나만 오므로 그것도 받아준다.
 * 알 수 없는 종류만 남으면 기타로 본다 — 종류가 없으면 상세에 그릴 것이 없다.
 */
export const partnerTypes = (partner) => {
  const raw = Array.isArray(partner?.types) && partner.types.length > 0
    ? partner.types
    : [partner?.type];
  const known = raw.filter(t => t && PARTNER_TYPE_LABEL[t]);
  return known.length > 0 ? known : ['ETC'];
};

/** 이 파트너가 그 종류에 해당하는가 (ALL은 항상 참) */
export const hasPartnerType = (partner, type) => (
  !type || type === 'ALL' || partnerTypes(partner).includes(type)
);

/** 백엔드 미배포 구간을 화면에서 구분하기 위한 헬퍼 */
export const isPartnerApiUnavailable = (err) => {
  const s = err?.response?.status;
  return s === 404 || s === 405 || s === 501;
};

const extractList = (body, keys) => {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== 'object') return null;
  for (const k of keys) if (Array.isArray(body[k])) return body[k];
  if (body.data) return extractList(body.data, keys);
  return null;
};

/**
 * 파트너 목록 (공개)
 * @param {{ type?: string, q?: string, region?: string, page?: number, limit?: number }} params
 */
export const getPartners = async ({ type = 'ALL', q = '', region = '', page = 1, limit = 24 } = {}) => {
  const res = await axiosInstance.get('/api/partners', {
    params: {
      type: type === 'ALL' ? undefined : type,
      q: String(q ?? '').trim() || undefined,
      region: region || undefined,
      page, limit,
    },
  });
  const body = res.data ?? {};
  const list = extractList(body, ['partners', 'content', 'list', 'items']);
  if (!list) throw new Error('파트너 목록 응답 형식을 해석할 수 없습니다.');
  const total = [body.total, body.totalElements, body.totalCount, body.count].find(v => Number.isFinite(v));
  return { partners: list, total: total ?? list.length };
};

/** 파트너 상세 (공개) — slug 또는 숫자 ID */
export const getPartnerDetail = async (slugOrId) => {
  const res = await axiosInstance.get(`/api/partners/${slugOrId}`);
  return res.data;
};

// ── 어드민 ────────────────────────────────────────────────────────────────────

/**
 * 어드민 목록 — 공개 API와 달리 published=false 도 포함한다.
 * 숨김 처리한 파트너를 관리 화면에서 다시 켜야 하므로 별도 엔드포인트를 쓴다.
 */
export const getAdminPartners = async ({ type = 'ALL', q = '', page = 1, limit = 100 } = {}) => {
  const res = await axiosInstance.get('/api/admin/partners', {
    params: {
      type: type === 'ALL' ? undefined : type,
      q: String(q ?? '').trim() || undefined,
      page, limit,
    },
  });
  const body = res.data ?? {};
  const list = extractList(body, ['partners', 'content', 'list', 'items']);
  if (!list) throw new Error('파트너 목록 응답 형식을 해석할 수 없습니다.');
  const total = [body.total, body.totalElements, body.totalCount, body.count].find(v => Number.isFinite(v));
  return { partners: list, total: total ?? list.length };
};

/**
 * 어드민 상세 조회 — 비공개(published=false) 파트너도 편집해야 하므로
 * 전용 엔드포인트를 먼저 시도하고, 없으면 공개 상세로 폴백한다.
 */
export const getAdminPartnerDetail = async (id) => {
  try {
    const res = await axiosInstance.get(`/api/admin/partners/${id}`);
    return res.data;
  } catch (err) {
    if (!isPartnerApiUnavailable(err)) throw err;
    return getPartnerDetail(id);
  }
};

export const createPartner = async (data) => {
  const res = await axiosInstance.post('/api/admin/partners', data);
  return res.data;
};

export const updatePartner = async (id, data) => {
  const res = await axiosInstance.put(`/api/admin/partners/${id}`, data);
  return res.data;
};

export const deletePartner = async (id) => {
  await axiosInstance.delete(`/api/admin/partners/${id}`);
};

/** 노출/숨김 전환 */
export const updatePartnerVisibility = async (id, published) => {
  const res = await axiosInstance.patch(`/api/admin/partners/${id}/visibility`, { published });
  return res.data;
};
